// Forms Platform F1 — hostname-aware routing (forms-platform.md §3.5,
// Grant's decision #1: forms.stewardship.pro from day one).
//
// The public form front door is a custom domain on the SAME Pages
// project as the OS. This helper is the single gate deciding "is this
// pageload on the public forms host?" — when true, App renders ONLY the
// public form routes (no OS shell, no session boot, no sign-in). The
// OS's own hosts keep serving /f/* too (staging tests, previews).
//
// Pure + exported for tests.

export const PUBLIC_FORMS_HOSTNAME = "forms.stewardship.pro";

/** Hosts that previously served client form links and may still be sitting in
 *  a client's inbox. They render the same public-only shell so an already-sent
 *  link keeps working after the front door moves.
 *
 *  insrenewal.stewardship.pro was the renewal intake form's original host. It
 *  is retired in favour of forms.stewardship.pro (the OS mints every new link
 *  there), but ~25 links carrying it were emailed to clients in August 2026
 *  and run until mid-September. Keeping it listed costs nothing and means a
 *  client who kept the email is not sent a dead end. Safe to delete once no
 *  unexpired instance carries it. */
export const LEGACY_PUBLIC_FORMS_HOSTNAMES = ["insrenewal.stewardship.pro"];

/** Additional live front doors for the same public-form routes.
 *
 *  private-money-loan-desk.md §8.6 asked for "a second hostname on that
 *  host" so the Private Money applications can sit on an address of their
 *  own. Adding one is TWO steps and this list is the second:
 *
 *    1. Point the name at the Pages project and add it as a custom domain
 *       (Cloudflare — a person with credentials, never a session).
 *    2. Add it here, so App renders the public-only shell for it instead
 *       of booting the OS.
 *
 *  It is a list rather than a constant precisely so step 2 is one line.
 *  Note that what a form LOOKS like does not depend on this at all: a
 *  brand follows its template (shared/forms/form-branding.ts), so the
 *  KlinkLoans application already wears the KlinkLoans wordmark on
 *  forms.stewardship.pro, on staging, and in a preview. The hostname is a
 *  nicer address, not a different page — which is what makes step 1 safe
 *  to do whenever Grant picks a name, with nothing to re-test. */
export const ADDITIONAL_PUBLIC_FORMS_HOSTNAMES: string[] = [
  "apply.stewardship.pro",
];

export function isPublicFormsHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return (
    host === PUBLIC_FORMS_HOSTNAME ||
    ADDITIONAL_PUBLIC_FORMS_HOSTNAMES.includes(host) ||
    LEGACY_PUBLIC_FORMS_HOSTNAMES.includes(host)
  );
}
